import React from 'react';
import { Animated, StyleProp, StyleSheet, TextStyle, TouchableOpacity, View, ViewStyle } from 'react-native';

import { useNavigation } from '@react-navigation/native';

import { CloseSmallIcon } from '@/assets/icons/CloseSmallIcon';
import BackArrow from '@/assets/icons/backArrow';
import { CloseIcon } from '@/assets/icons/close';
import { LineSeparator } from '@/components/LineSeparator';
import { ds } from '@/components/lib/design.system';
import { common } from '@/styles/styles';

import { SizedBox } from './SizedBox';
import { StyleVariant, Text } from './TextComponent';

export const SCREEN_NAV_BAR_HEIGHT = 60;
export const SHEET_NAV_BAR_TOP_SPACING = 10;

export type ScreenNavBarProps = {
    title?: string;
    subTitle?: string | React.ReactNode;
    rightIcon?: React.ReactNode;
    titleOverrideStyle?: StyleProp<TextStyle>;
    titleVariant?: StyleVariant;
    closeIconMode?: 'back' | 'close' | 'none';
    onClose?: () => void;
    scrollAnim?: React.MutableRefObject<Animated.Value>;
    style?: StyleProp<ViewStyle>;
    secondaryBar?: React.ReactNode;
    handlePressTracking?: () => void;
    showNavbarTitle?: boolean;
    alignTitleLeft?: boolean;
    height?: number;
    footer?: React.ReactNode;
    closeIcon?: React.ReactNode;
    backIcon?: React.ReactNode;
    // flag indicating if this is the header of a sheet
    sheet?: boolean;
    closeDisabled?: boolean;
};

export const ScreenNavBar = ({
    title = '',
    subTitle,
    rightIcon = null,
    titleOverrideStyle,
    titleVariant,
    closeIconMode = 'back',
    onClose,
    scrollAnim,
    style,
    secondaryBar,
    handlePressTracking,
    showNavbarTitle = true,
    alignTitleLeft = false,
    height,
    footer,
    closeIcon,
    backIcon = <BackArrow />,
    sheet = false,
    closeDisabled = false,
}: ScreenNavBarProps) => {
    const navigation = useNavigation();
    const defaultCloseIcon = sheet ? <CloseIcon testID="closeIcon" /> : <CloseSmallIcon testID="closeIcon" />;

    const handleOnClose = () => {
        handlePressTracking?.();
        if (onClose) {
            onClose();
        } else {
            navigation.goBack();
        }
    };

    const renderTitleBlock = (textAlign: 'center' | 'left') => {
        if (!showNavbarTitle) {
            return null;
        }
        return (
            <View testID={`screenTitle-${title.replace(/\s/g, '_')}`}>
                <Text
                    variant={titleVariant || 'titleLarge'}
                    style={[titleOverrideStyle, alignTitleLeft ? styles.leftTitleContainer : {}]}
                    textAlign={textAlign}
                    color={ds.colorToken.text.primary}
                >
                    {title}
                </Text>
                {subTitle ? (
                    <>
                        {typeof subTitle === 'string' ? (
                            <Text variant={'bodySmall2'} color={'neutral300'} textAlign={'center'}>
                                {subTitle}
                            </Text>
                        ) : (
                            <>
                                <SizedBox value={5} />
                                {subTitle}
                            </>
                        )}
                    </>
                ) : null}
            </View>
        );
    };

    return (
        <>
            <View
                style={[
                    common.spaceBetweenRow,
                    common.paddingHorizontal,
                    styles.container,
                    sheet ? styles.sheetContainer : undefined,
                    style,
                    height ? { height } : undefined,
                ]}
            >
                <View style={alignTitleLeft ? styles.leftContainerAligned : [common.flex, common.alignStart]}>
                    <TouchableOpacity onPress={handleOnClose} disabled={closeDisabled} testID="backArrow" hitSlop={12}>
                        {closeIconMode === 'close' ? closeIcon ?? defaultCloseIcon : null}
                        {closeIconMode === 'back' ? backIcon : null}
                    </TouchableOpacity>
                    {alignTitleLeft ? <View style={styles.leftTitleContainer}>{renderTitleBlock('left')}</View> : null}
                </View>

                {!alignTitleLeft ? <View style={[common.alignCenter]}>{renderTitleBlock('center')}</View> : null}

                <View style={common.flex}>
                    {rightIcon ? <View style={common.alignEnd}>{rightIcon}</View> : <SizedBox value={5} />}
                </View>
            </View>
            {secondaryBar}
            {scrollAnim ? <LineSeparator style={{ opacity: scrollAnim.current }} /> : null}
            {footer}
        </>
    );
};

const styles = StyleSheet.create({
    container: {
        height: SCREEN_NAV_BAR_HEIGHT,
    },
    sheetContainer: {
        marginTop: SHEET_NAV_BAR_TOP_SPACING,
    },
    leftTitleContainer: {
        marginLeft: 10,
    },
    leftContainerAligned: {
        flexDirection: 'row',
        alignItems: 'center',
    },
});
